import * as THREE from 'three';
import { SCATTER } from '../../config/tuning';
import type { Rng } from '../../core/Rng';
import { mergeGeometries } from './VegetationGen';

/**
 * Parametric buildings (SPEC §5.1, §3.9.2): footprint → extrusion → roof, with per-instance
 * colour and height variation, from six archetypes.
 *
 * The archetypes are chosen to give a *coastal town* silhouette rather than a generic block of
 * boxes: low houses with gables, a couple of taller flat-roofed blocks, warehouses at the quay,
 * and the church tower that makes the settlement legible as a place from three kilometres out.
 */

export const BuildingArchetype = {
  House: 0,
  Terrace: 1,
  Block: 2,
  Warehouse: 3,
  Tower: 4,
  Shed: 5,
} as const;

export const BUILDING_ARCHETYPE_COUNT = 6;

interface ArchetypeSpec {
  widthM: [number, number];
  depthM: [number, number];
  floors: [number, number];
  roof: 'flat' | 'gable' | 'hip';
}

const SPECS: readonly ArchetypeSpec[] = [
  { widthM: [7, 11], depthM: [6, 10], floors: [1, 2], roof: 'gable' },
  { widthM: [14, 22], depthM: [7, 9], floors: [2, 3], roof: 'gable' },
  { widthM: [12, 18], depthM: [10, 16], floors: [3, 5], roof: 'flat' },
  { widthM: [20, 34], depthM: [12, 20], floors: [1, 1], roof: 'hip' },
  { widthM: [6, 8], depthM: [6, 8], floors: [5, 7], roof: 'hip' },
  { widthM: [5, 8], depthM: [4, 6], floors: [1, 1], roof: 'flat' },
];

/** Build one building of an archetype. Deterministic given the generator's state. */
export function createBuildingGeometry(archetype: number, rng: Rng): THREE.BufferGeometry {
  const spec = SPECS[archetype % SPECS.length];
  const width = rng.range(spec.widthM[0], spec.widthM[1]);
  const depth = rng.range(spec.depthM[0], spec.depthM[1]);
  const floors = rng.int(spec.floors[0], spec.floors[1]);
  const wallHeight = floors * SCATTER.floorHeightM;

  const parts: THREE.BufferGeometry[] = [];

  const walls = new THREE.BoxGeometry(width, wallHeight, depth);
  walls.translate(0, wallHeight / 2, 0);
  parts.push(walls);

  if (spec.roof === 'gable') {
    parts.push(createGableRoof(width, depth, wallHeight, width * SCATTER.gablePitch));
  } else if (spec.roof === 'hip') {
    parts.push(createHipRoof(width, depth, wallHeight, width * SCATTER.gablePitch * 0.7));
  } else {
    // Flat roofs get a parapet, which is what stops a block reading as an untextured cube.
    const parapet = new THREE.BoxGeometry(width * 1.04, SCATTER.parapetM, depth * 1.04);
    parapet.translate(0, wallHeight + SCATTER.parapetM / 2, 0);
    parts.push(parapet);
  }

  return mergeGeometries(parts);
}

/** A prism roof: two slopes meeting at a ridge along the building's long axis. */
function createGableRoof(
  width: number,
  depth: number,
  baseY: number,
  riseM: number,
): THREE.BufferGeometry {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const top = baseY + riseM;

  // Six vertices: four eaves corners and two ridge ends.
  const positions = new Float32Array([
    -halfWidth, baseY, -halfDepth,
    halfWidth, baseY, -halfDepth,
    halfWidth, baseY, halfDepth,
    -halfWidth, baseY, halfDepth,
    0, top, -halfDepth,
    0, top, halfDepth,
  ]);
  const indices = new Uint16Array([
    0, 4, 5, 0, 5, 3, // north slope
    1, 2, 5, 1, 5, 4, // south slope
    0, 1, 4, // west gable
    3, 5, 2, // east gable
  ]);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(12), 2));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  return geometry;
}

/** A hip roof: four slopes meeting at a short ridge. */
function createHipRoof(
  width: number,
  depth: number,
  baseY: number,
  riseM: number,
): THREE.BufferGeometry {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const ridgeHalf = halfWidth * 0.35;
  const top = baseY + riseM;

  const positions = new Float32Array([
    -halfWidth, baseY, -halfDepth,
    halfWidth, baseY, -halfDepth,
    halfWidth, baseY, halfDepth,
    -halfWidth, baseY, halfDepth,
    -ridgeHalf, top, 0,
    ridgeHalf, top, 0,
  ]);
  const indices = new Uint16Array([
    0, 1, 5, 0, 5, 4, // north
    2, 3, 4, 2, 4, 5, // south
    3, 0, 4, // west
    1, 2, 5, // east
  ]);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(12), 2));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  return geometry;
}
